import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import type { LucideIcon } from 'lucide-react';
import { Menu, X, ChevronDown, Eye, ShoppingCart, Target, Search, TrendingUp, Building2, FileText, Scale, Wrench, FlaskConical, LineChart, Rocket, BookOpen, LayoutDashboard, ListChecks, ClipboardCheck, Mail, Waypoints, Bot, BarChart3, ImageIcon, MessageSquareText, MousePointerClick, Split, Frame } from "lucide-react";
import { EcMark } from "@/components/icons/EcMark";
import { supabase } from '@/integrations/supabase/client';
import { motion, AnimatePresence } from 'framer-motion';
import { EyeCaptainLogo } from "@/components/brand/EyeCaptainLogo";
import { useLanguage } from '@/contexts/LanguageContext';
// LanguageSwitcher removed — site is English-only
import { useIsMobile } from '@/hooks/use-mobile';
import { HOME_THEME, MONO } from '@/components/home-a/theme';
import { useLiveReleases } from '@/hooks/useFeatureAccess';

interface MenuItem {
  href: string;
  labelKey: string;
  label?: string;
  icon?: LucideIcon;
  descriptionKey?: string;
  description?: string;
  highlighted?: boolean;
  highlightColor?: string;
  /** Release key (Admin › Releases): the item stays out of the menu until that release is live. */
  release?: string;
}

interface MenuSection {
  titleKey: string;
  title?: string;
  items: MenuItem[];
}

interface NavItem {
  href?: string;
  labelKey: string;
  label?: string;
  isSimpleDropdown?: boolean;
  isTabbedMenu?: boolean;
  megaMenu?: {
    sections: MenuSection[];
    featured?: {
      titleKey: string;
      descriptionKey: string;
      ctaKey: string;
      href: string;
      gradient: string;
      icon: LucideIcon;
    };
  };
  tabs?: {
    key: string;
    labelKey: string;
    icon: LucideIcon;
    sections: MenuSection[];
    banner: {
      titleKey: string;
      descriptionKey: string;
      ctaKey: string;
      href: string;
      gradient: string;
      icon: LucideIcon;
    };
  }[];
}

// Base paths (EN) - getLocalizedPath will handle localization
const getNavItems = (t: (key: string) => string): NavItem[] => [
  {
    // The site-type and platform pages (/cro-page-audit, /solutions/saas,
    // /shopify-cro-audit, /woocommerce-cro-audit ...) are for search only: they
    // are linked from the footer, the sitemap and related pages, not from here.
    labelKey: 'megamenu.solutions',
    megaMenu: {
      sections: [
        {
          titleKey: 'megamenu.analysisTypes',
          items: [
            { href: '/solutions/ecommerce', labelKey: 'megamenu.ecommerce', icon: ShoppingCart, descriptionKey: 'megamenu.ecommerceDesc', highlighted: true, highlightColor: 'darkblue' },
            { href: '/solutions/lead-generation', labelKey: 'megamenu.leadgen', icon: Target, descriptionKey: 'megamenu.leadgenDesc', highlighted: true, highlightColor: 'purple' },
            { href: '/features/ai-heatmaps', labelKey: 'megamenu.aiHeatmaps', icon: Eye, descriptionKey: 'megamenu.aiHeatmapsDesc' },
            { href: '/solutions/content-analysis', labelKey: 'megamenu.contentAnalysis', icon: FileText, descriptionKey: 'megamenu.contentAnalysisDesc' },
            { href: '/solutions/technical-analysis', labelKey: 'megamenu.technicalAnalysis', icon: Wrench, descriptionKey: 'megamenu.technicalAnalysisDesc' },
            { href: '/solutions/competitor-comparison', labelKey: 'megamenu.competitorComparison', icon: Scale, descriptionKey: 'megamenu.competitorComparisonDesc' },
            { href: '/solutions/funnel-analysis', labelKey: 'megamenu.funnel', icon: Waypoints, descriptionKey: 'megamenu.funnelDesc', release: 'funnel' },
            { href: '/solutions/user-agent', labelKey: 'megamenu.userAgent', icon: Bot, descriptionKey: 'megamenu.userAgentDesc', release: 'user_agent' },
            { href: '/features/figma-design-audit', labelKey: 'megamenu.figmaDesign', icon: Frame, descriptionKey: 'megamenu.figmaDesignDesc', release: 'figma_design' },
          ]
        },
        {
          titleKey: 'megamenu.dataAndAi',
          items: [
            { href: '/features/analytics-integrations', labelKey: 'megamenu.analyticsIntegrations', icon: BarChart3, descriptionKey: 'megamenu.analyticsIntegrationsDesc', release: 'connected_apps' },
            { href: '/features/visual-mockups', labelKey: 'megamenu.visualMockups', icon: ImageIcon, descriptionKey: 'megamenu.visualMockupsDesc', release: 'suggestion_mockup' },
            { href: '/features/ai-agent', labelKey: 'megamenu.aiAgent', icon: MessageSquareText, descriptionKey: 'megamenu.aiAgentDesc', release: 'ai_agent' },
            { href: '/features/live-heatmaps', labelKey: 'megamenu.liveHeatmaps', icon: MousePointerClick, descriptionKey: 'megamenu.liveHeatmapsDesc', release: 'live_heatmaps' },
            { href: '/features/ab-testing', labelKey: 'megamenu.abTestingFeature', icon: Split, descriptionKey: 'megamenu.abTestingFeatureDesc', release: 'ab_testing' },
          ]
        },
        {
          titleKey: 'megamenu.platform',
          items: [
            { href: '/what-is-eyecaptain', labelKey: 'megamenu.whatIsEyeCaptain', icon: EcMark, descriptionKey: 'megamenu.whatIsEyeCaptainDesc' },
            { href: '/how-it-works', labelKey: 'megamenu.howItWorks', icon: LineChart, descriptionKey: 'megamenu.howItWorksDesc' },
          ]
        }
      ],
      featured: {
        titleKey: 'megamenu.agenciesFeaturedTitle',
        descriptionKey: 'megamenu.agenciesFeaturedDesc',
        ctaKey: 'megamenu.agenciesFeaturedCta',
        href: '/agencies',
        gradient: 'from-primary to-accent',
        icon: Building2,
      }
    }
  },
  { href: '/agencies', labelKey: 'nav.agencies' },
  { href: '/pricing', labelKey: 'nav.pricing' },
  {
    labelKey: 'megamenu.resourcesHub',
    megaMenu: {
      sections: [
        {
          titleKey: 'megamenu.croHubLearn',
          items: [
            { href: '/what-is-cro', labelKey: 'megamenu.whatIsCro', icon: TrendingUp, descriptionKey: 'megamenu.whatIsCroDesc' },
            { href: '/cro-audit', labelKey: 'megamenu.croAuditGuide', icon: ClipboardCheck, descriptionKey: 'megamenu.croAuditGuideDesc' },
            { href: '/cro-checklist', labelKey: 'megamenu.croChecklist', icon: ListChecks, descriptionKey: 'megamenu.croChecklistDesc' },
            { href: '/ecommerce-cro-guide', labelKey: 'megamenu.ecommerceCro', icon: ShoppingCart, descriptionKey: 'megamenu.ecommerceCroDesc' },
            { href: '/ab-testing-guide', labelKey: 'megamenu.abTesting', icon: FlaskConical, descriptionKey: 'megamenu.abTestingDesc' },
          ]
        },
        {
          titleKey: 'megamenu.croHubTools',
          items: [
            { href: '/blog', labelKey: 'nav.blog', icon: BookOpen, descriptionKey: 'megamenu.blogDesc' },
            { href: '/faq', labelKey: 'nav.faq', icon: Search, descriptionKey: 'megamenu.faqDesc' },
            { href: '/contact', labelKey: 'nav.contact', icon: Mail, descriptionKey: 'megamenu.contactDesc' },
          ]
        }
      ],
      featured: {
        titleKey: 'megamenu.freeWebsiteAudit',
        descriptionKey: 'megamenu.freeWebsiteAuditDesc',
        ctaKey: 'nav.freeCro',
        href: '/free-audit',
        gradient: 'from-accent to-primary',
        icon: Rocket,
      }
    }
  },
];

export default function MegaMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [mobileExpandedMenus, setMobileExpandedMenus] = useState<string[]>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { t, getLocalizedPath } = useLanguage();
  const isMobile = useIsMobile();
  
  // Items tied to a release (Admin › Releases) stay out of the menu until it is live. Their
  // pages are for search until then: linked from the footer, the sitemap and related pages.
  const { data: liveReleases } = useLiveReleases();
  const navItems = getNavItems(t).map((nav) =>
    nav.megaMenu
      ? {
          ...nav,
          megaMenu: {
            ...nav.megaMenu,
            sections: nav.megaMenu.sections.map((sec) => ({
              ...sec,
              items: sec.items.filter((it) => !it.release || liveReleases?.has(it.release)),
            })).filter((sec) => sec.items.length > 0),
          },
        }
      : nav,
  );
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsLoggedIn(!!session);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsLoggedIn(!!session);
    });
    return () => subscription.unsubscribe();
  }, []);

  const goToFreeCroAnalysis = () => {
    navigate(getLocalizedPath('/free-audit'));
    setIsOpen(false);
  };

  const toggleMobileMenu = (labelKey: string) => {
    setMobileExpandedMenus(prev => 
      prev.includes(labelKey) 
        ? prev.filter(key => key !== labelKey)
        : [...prev, labelKey]
    );
  };

  const isActive = (href: string) => {
    const localizedHref = getLocalizedPath(href);
    return location.pathname === localizedHref;
  };

  useEffect(() => {
    setActiveMenu(null);
    setIsOpen(false);
  }, [location]);

  // The mobile menu is a full-screen sheet; the page behind it must not scroll.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActiveMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMouseEnter = (labelKey: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveMenu(labelKey);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveMenu(null);
    }, 150);
  };

  return (
    <nav style={HOME_THEME} className="fixed top-0 left-0 right-0 z-50 text-foreground" ref={menuRef}>
      {/* Sits on the hero with no bar of its own; once the page scrolls the bar
          fades in (background, blur, hairline) and stays with you. Opaque on
          mobile because there is no blur there. */}
      <div
        aria-hidden
        className={`absolute inset-0 border-b transition-[background-color,border-color,backdrop-filter] duration-300 ${
          scrolled || isOpen || activeMenu
            ? `border-border/70 ${isMobile ? 'bg-background' : 'bg-background/80 backdrop-blur-xl'}`
            : 'border-transparent bg-transparent'
        }`}
      />
      
      <div className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 relative z-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo. Sized and animated in CSS only, not by screen width in JS: the
              header is in the prerendered HTML, which cannot know the screen. */}
          <Link to={getLocalizedPath('/')} className="flex items-center gap-2 group" aria-label="EyeCaptain home">
            <EyeCaptainLogo
              alt="EyeCaptain"
              width={192}
              height={48}
              className="h-9 w-auto transition-transform duration-200 md:h-12 md:group-hover:scale-105"
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <div
                key={item.labelKey}
                onMouseEnter={() => (item.megaMenu || item.tabs) && handleMouseEnter(item.labelKey)}
                onMouseLeave={handleMouseLeave}
              >
                {item.href ? (
                  <Link
                    to={getLocalizedPath(item.href)}
                    aria-haspopup={item.megaMenu ? true : undefined}
                    aria-expanded={item.megaMenu ? activeMenu === item.labelKey : undefined}
                    onFocus={() => item.megaMenu && handleMouseEnter(item.labelKey)}
                    className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-1 ${
                      isActive(item.href) || activeMenu === item.labelKey
                        ? 'bg-primary/10 text-primary'
                        : 'text-foreground/80 hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    {t(item.labelKey)}
                    {item.megaMenu && (
                      <motion.div animate={{ rotate: activeMenu === item.labelKey ? 180 : 0 }} transition={{ duration: 0.2 }}>
                        <ChevronDown className="w-4 h-4" />
                      </motion.div>
                    )}
                  </Link>
                ) : (
                  <button
                    className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-1 ${
                      activeMenu === item.labelKey
                        ? 'bg-primary/10 text-primary'
                        : 'text-foreground/80 hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    {t(item.labelKey)}
                    <motion.div
                      animate={{ rotate: activeMenu === item.labelKey ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </motion.div>
                  </button>
                )}

                <AnimatePresence>
                  {item.megaMenu && activeMenu === item.labelKey && (
                    <MegaPanel
                      item={item}
                      t={t}
                      getLocalizedPath={getLocalizedPath}
                      onEnter={() => handleMouseEnter(item.labelKey)}
                      onLeave={handleMouseLeave}
                    />
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Link to={isLoggedIn ? '/workspace' : getLocalizedPath('/login')}>
              <Button 
                variant={isLoggedIn ? "default" : "ghost"}
                size="sm"
                className={isLoggedIn 
                  ? "bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-full px-5 shadow-lg shadow-primary/25"
                  : "text-foreground/80 hover:text-foreground font-medium"
                }
              >
                {isLoggedIn ? (
                  <span className="flex items-center gap-1.5">
                    <LayoutDashboard className="w-4 h-4" />
                    {t('nav.dashboard')}
                  </span>
                ) : t('nav.login')}
              </Button>
            </Link>
            {!isLoggedIn && (
              <Button 
                size="sm"
                onClick={goToFreeCroAnalysis}
                className="ec-cta-white font-semibold rounded-full px-5"
              >
                {t('megamenu.freeAudit')}

              </Button>
            )}
          </div>

          {/* Mobile: the primary action stays one tap away, next to the menu */}
          <div className="flex items-center gap-1.5 lg:hidden">
          {isLoggedIn ? (
            <Link
              to="/workspace"
              className="ec-cta-white inline-flex h-10 items-center gap-1.5 rounded-full px-4 text-sm font-semibold"
            >
              <LayoutDashboard className="w-4 h-4" aria-hidden="true" />
              {t('nav.dashboard')}
            </Link>
          ) : (
            !isOpen && (
              // Signup lives inside the menu; the header keeps the free audit,
              // the one action a first-time visitor came for.
              <button
                type="button"
                onClick={goToFreeCroAnalysis}
                data-track="header_free_audit"
                className="ec-cta-white inline-flex h-10 items-center gap-1 rounded-full px-4 text-sm font-semibold"
              >
                {t('megamenu.freeAudit')}
              </button>
            )
          )}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden inline-flex h-11 w-11 items-center justify-center text-foreground hover:bg-muted/50 rounded-lg transition-colors"
            aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isOpen}
          >
            {isOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
          </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, x: '100%' }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="lg:hidden fixed bottom-0 right-0 top-16 md:top-20 w-full sm:w-96 bg-background bg-[radial-gradient(ellipse_80%_40%_at_100%_0%,hsl(var(--primary)/0.10),transparent_70%)] border-l border-border shadow-2xl z-50 flex flex-col"
            >
              <div className="flex-1 overflow-y-auto overscroll-contain px-3 py-2">
                {navItems.map((item) => (
                  <div key={item.labelKey}>
                    {item.href && !item.megaMenu ? (
                      <Link
                        to={getLocalizedPath(item.href)}
                        onClick={() => setIsOpen(false)}
                        className={`flex items-center justify-between border-b border-border/70 px-3 py-4 text-[17px] font-medium transition-colors ${
                          isActive(item.href)
                            ? 'text-[hsl(var(--ec-mint))]'
                            : 'text-foreground hover:text-[hsl(var(--ec-mint))]'
                        }`}
                      >
                        {t(item.labelKey)}
                      </Link>
                    ) : (
                      <div>
                        <button
                          onClick={() => toggleMobileMenu(item.labelKey)}
                          aria-expanded={mobileExpandedMenus.includes(item.labelKey)}
                          className={`flex w-full items-center justify-between border-b border-border/70 px-3 py-4 text-[17px] font-medium transition-colors ${mobileExpandedMenus.includes(item.labelKey) ? 'text-[hsl(var(--ec-mint))]' : 'text-foreground'}`}
                        >
                          <span>{t(item.labelKey)}</span>
                          <motion.div
                            animate={{ rotate: mobileExpandedMenus.includes(item.labelKey) ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <ChevronDown className="w-4 h-4" />
                          </motion.div>
                        </button>
                        <AnimatePresence>
                          {mobileExpandedMenus.includes(item.labelKey) && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              {item.megaMenu?.sections.map((section, idx) => (
                                <div key={section.titleKey} className="px-2 pb-2">
                                  <p className="px-3 pb-1 pt-2 text-[10.5px] uppercase tracking-[0.12em] text-[hsl(var(--ec-muted))]" style={{ fontFamily: MONO }}>
                                    <span className="text-[hsl(var(--ec-mint))]">0{idx + 1}</span>&nbsp;&nbsp;{t(section.titleKey)}
                                  </p>
                                  {section.items.map((menuItem) => (
                                    <Link
                                      key={menuItem.href}
                                      to={getLocalizedPath(menuItem.href)}
                                      onClick={() => setIsOpen(false)}
                                      className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] text-foreground/90 transition-colors hover:bg-primary/[0.07]"
                                    >
                                      {menuItem.icon && (
                                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px] border border-border bg-foreground/[0.03] text-[hsl(var(--ec-mint))]">
                                          <menuItem.icon className="h-4 w-4" />
                                        </span>
                                      )}
                                      {t(menuItem.labelKey)}
                                    </Link>
                                  ))}
                                </div>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                  </div>
                ))}
                <Link
                  to={getLocalizedPath('/free-audit')}
                  onClick={() => setIsOpen(false)}
                  className="mt-5 block rounded-2xl border border-[hsl(var(--ec-line-strong))] bg-[linear-gradient(160deg,hsl(var(--primary)/0.14),hsl(var(--card))_65%)] p-4"
                >
                  <p className="text-[10.5px] uppercase tracking-[0.12em] text-[hsl(var(--ec-mint))]" style={{ fontFamily: MONO }}>Free · 5 min</p>
                  <p className="mt-1.5 text-[16px] font-semibold">{t('megamenu.freeWebsiteAudit')}</p>
                  <div className="mt-3"><AuditVisual /></div>
                </Link>
              </div>
              <div className="space-y-2 border-t border-border bg-card/80 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur">
                {!isLoggedIn && (
                  <p className="pb-1 text-center text-[12px] text-[hsl(var(--ec-muted))]">{t('nav.freeSignupHint')}</p>
                )}
                <div className={isLoggedIn ? '' : 'grid grid-cols-2 gap-2'}>
                  <Link
                    to={isLoggedIn ? '/workspace' : getLocalizedPath('/login')}
                    onClick={() => setIsOpen(false)}
                    className={isLoggedIn
                      ? 'ec-cta flex h-12 w-full items-center justify-center gap-1.5 rounded-[10px] text-[15px] font-semibold'
                      : 'flex h-12 items-center justify-center rounded-[10px] border border-[hsl(var(--ec-line-strong))] text-[15px] font-medium text-foreground'}
                  >
                    {isLoggedIn && <LayoutDashboard className="h-4 w-4" />}
                    {isLoggedIn ? t('nav.dashboard') : t('nav.login')}
                  </Link>
                  {!isLoggedIn && (
                    <Link
                      to={getLocalizedPath('/signup')}
                      onClick={() => setIsOpen(false)}
                      className="ec-cta flex h-12 items-center justify-center gap-1.5 rounded-[10px] text-[15px] font-semibold"
                    >
                      {t('nav.freeSignup')}

                    </Link>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
}

/* ------------------------------------------------------------------ */
/* Desktop dropdown                                                    */
/* ------------------------------------------------------------------ */

type Translate = (key: string) => string;

/** A small score ring, the same visual language as the report. */
function MiniRing({ score, size = 64 }: { score: number; size?: number }) {
  const r = size / 2 - 5;
  const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--border))" strokeWidth="4" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="hsl(var(--ec-mint))"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray={`${(score / 100) * c} ${c}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x="50%" y="54%" textAnchor="middle" dominantBaseline="middle" fill="hsl(var(--foreground))" fontSize={size * 0.3} fontWeight="600">
        {score}
      </text>
    </svg>
  );
}

/** Agencies: three client pages, each with its score. */
function AgencyVisual() {
  const rows: [string, number][] = [["client-a.com", 72], ["client-b.shop", 58], ["client-c.io", 81]];
  return (
    <div className="space-y-1.5">
      {rows.map(([name, score]) => (
        <div key={name} className="flex items-center gap-2.5 rounded-lg border border-border bg-background/50 px-2.5 py-2">
          <span className="grid h-6 w-6 place-items-center rounded-md bg-primary/15 text-[10px] font-semibold text-[hsl(var(--ec-mint))]">{name[7].toUpperCase()}</span>
          <span className="flex-1 truncate text-[11.5px] text-[hsl(var(--ec-text-2))]" style={{ fontFamily: MONO }}>{name}</span>
          <span className="h-1.5 w-12 overflow-hidden rounded-full bg-foreground/[0.06]">
            <span className="block h-full rounded-full bg-[linear-gradient(90deg,hsl(var(--primary)),hsl(var(--ec-mint)))]" style={{ width: `${score}%` }} />
          </span>
          <span className="w-5 text-right text-[11.5px] font-semibold" style={{ fontFamily: MONO }}>{score}</span>
        </div>
      ))}
    </div>
  );
}

/** Free audit: a score and the first fixes. */
function AuditVisual() {
  const fixes = ["Buy button too quiet", "Returns folded away", "No reviews by price"];
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-background/50 p-3">
      <MiniRing score={64} size={60} />
      <ul className="min-w-0 flex-1 space-y-1.5">
        {fixes.map((f, i) => (
          <li key={f} className="flex items-center gap-1.5 text-[11px] text-[hsl(var(--ec-text-2))]">
            <span className="grid h-4 w-4 shrink-0 place-items-center rounded-full bg-primary/20 text-[9px] font-semibold text-[hsl(var(--ec-mint))]">{i + 1}</span>
            <span className="truncate">{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MegaPanel({
  item,
  t,
  getLocalizedPath,
  onEnter,
  onLeave,
}: {
  item: NavItem;
  t: Translate;
  getLocalizedPath: (p: string) => string;
  onEnter: () => void;
  onLeave: () => void;
}) {
  const menu = item.megaMenu!;
  const featured = menu.featured;
  const isAudit = featured?.href === '/free-audit';
  return (
    <div
      className="absolute left-1/2 top-full w-[min(900px,calc(100vw-48px))] -translate-x-1/2 pt-2"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 6 }}
      transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="overflow-hidden rounded-[20px] border border-[hsl(var(--ec-line-strong))] bg-card/95 shadow-[0_32px_80px_-24px_rgba(0,0,0,0.9)] backdrop-blur-xl">
        <div className="grid grid-cols-[minmax(0,1fr)_280px]">
          <div className="space-y-5 p-5">
            {menu.sections.map((section, idx) => (
              <div key={section.titleKey}>
                <p className="mb-2 px-2 text-[11px] uppercase tracking-[0.12em] text-[hsl(var(--ec-muted))]" style={{ fontFamily: MONO }}>
                  <span className="text-[hsl(var(--ec-mint))]">0{idx + 1}</span>&nbsp;&nbsp;{t(section.titleKey)}
                </p>
                <div className="grid grid-cols-2 gap-1">
                  {section.items.map((menuItem) => {
                    const Icon = menuItem.icon;
                    return (
                      <Link
                        key={menuItem.href}
                        to={getLocalizedPath(menuItem.href)}
                        className="group flex items-start gap-3 rounded-xl p-2.5 transition-colors hover:bg-primary/[0.07]"
                      >
                        {Icon && (
                          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] border border-border bg-foreground/[0.03] text-[hsl(var(--ec-mint))] transition-colors group-hover:border-[hsl(var(--ec-mint)/0.4)] group-hover:bg-primary/10">
                            <Icon className="h-4 w-4" />
                          </span>
                        )}
                        <span className="min-w-0">
                          <span className="flex items-center gap-1.5 text-[14px] font-medium text-foreground">
                            {t(menuItem.labelKey)}
                            {menuItem.highlighted && (
                              <span className="rounded-full border border-[hsl(var(--ec-mint)/0.35)] bg-primary/10 px-1.5 py-px text-[9.5px] uppercase tracking-wider text-[hsl(var(--ec-mint))]" style={{ fontFamily: MONO }}>
                                {menuItem.href === '/free-audit' ? 'Free' : 'Core'}
                              </span>
                            )}
                          </span>
                          {menuItem.descriptionKey && (
                            <span className="mt-0.5 block text-[12.5px] leading-snug text-[hsl(var(--ec-muted))]">{t(menuItem.descriptionKey)}</span>
                          )}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {featured && (
            <Link
              to={getLocalizedPath(featured.href)}
              className="group relative flex flex-col border-l border-border bg-[linear-gradient(165deg,hsl(var(--primary)/0.16),transparent_62%)] p-5"
            >
              <span aria-hidden className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[radial-gradient(circle,hsl(var(--primary)/0.22),transparent_70%)]" />
              <p className="text-[11px] uppercase tracking-[0.12em] text-[hsl(var(--ec-mint))]" style={{ fontFamily: MONO }}>
                {isAudit ? 'Free · 5 min' : 'White-label reports'}
              </p>
              <h4 className="mt-2 text-[18px] font-semibold leading-snug tracking-[-0.01em]">{t(featured.titleKey)}</h4>
              <p className="mt-1.5 text-[13px] leading-relaxed text-[hsl(var(--ec-text-2))]">{t(featured.descriptionKey)}</p>
              <div className="mt-4">{isAudit ? <AuditVisual /> : <AgencyVisual />}</div>
              <span className="ec-cta mt-auto inline-flex h-10 items-center justify-center gap-1.5 rounded-[10px] px-4 text-[13.5px] font-semibold">
                {t(featured.ctaKey)}

              </span>
            </Link>
          )}
        </div>
        {!isAudit && <div className="flex items-center justify-between gap-4 border-t border-border bg-background/40 px-5 py-3 text-[12.5px]">
          <span className="text-[hsl(var(--ec-muted))]">No card · Result in under 5 minutes · Desktop and mobile</span>
          <Link to={getLocalizedPath('/free-audit')} className="inline-flex items-center gap-1 font-medium text-[hsl(var(--ec-mint))] hover:underline">
            {t('megamenu.freeAudit')}

          </Link>
        </div>}
      </div>
    </motion.div>
    </div>
  );
}
